import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '../common/Alert';

const ManagerForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'info' });
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    department: '',
    status: 'active'
  });

  useEffect(() => {
    if (id) {
      loadManager();
    }
  }, [id]);

  const loadManager = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/managers/${id}`, {
        credentials: 'same-origin'
      });
      if (response.ok) {
        const data = await response.json();
        setFormData({
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email,
          department: data.department,
          status: data.status || 'active'
        });
      } else {
        setAlert({ show: true, message: 'Failed to load manager', type: 'error' });
        setTimeout(() => navigate('/admin/managers'), 2000);
      }
    } catch (err) {
      console.error('Failed to load manager:', err);
      setAlert({ show: true, message: 'Failed to load manager', type: 'error' });
      setTimeout(() => navigate('/admin/managers'), 2000);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const url = id ? `/api/managers/${id}` : '/api/managers';
      const method = id ? 'PATCH' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({ manager: formData })
      });

      if (response.ok) {
        setAlert({ show: true, message: id ? 'Manager updated successfully!' : 'Manager created successfully!', type: 'success' });
        setTimeout(() => navigate('/admin/managers'), 1500);
      } else {
        const data = await response.json();
        setAlert({ show: true, message: `Failed to save manager: ${data.errors ? data.errors.join(', ') : 'Unknown error'}`, type: 'error' });
      }
    } catch (err) {
      console.error('Error saving manager:', err);
      setAlert({ show: true, message: 'Failed to save manager', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      padding: '2rem',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      maxWidth: '600px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '2rem',
      fontWeight: 'bold',
      color: '#2c3e50',
      margin: 0,
      marginBottom: '0.5rem'
    },
    subtitle: {
      color: '#6c757d',
      margin: 0
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1.5rem'
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem'
    },
    label: {
      fontWeight: '600',
      color: '#2c3e50'
    },
    input: {
      padding: '0.75rem',
      border: '1px solid #dee2e6',
      borderRadius: '4px',
      fontSize: '1rem'
    },
    buttonGroup: {
      display: 'flex',
      gap: '1rem',
      marginTop: '1rem'
    },
    submitBtn: {
      padding: '0.75rem 1.5rem',
      backgroundColor: '#07638e',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '1rem',
      fontWeight: '500'
    },
    cancelBtn: {
      padding: '0.75rem 1.5rem',
      backgroundColor: '#6c757d',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '1rem',
      fontWeight: '500'
    },
    disabledBtn: {
      opacity: 0.6,
      cursor: 'not-allowed'
    }
  };

  return (
    <div style={styles.container}>
      <Alert 
        show={alert.show}
        message={alert.message}
        type={alert.type}
        onClose={() => setAlert({ ...alert, show: false })}
      />
      <div style={styles.header}>
        <h1 style={styles.title}>{id ? 'Edit Manager' : 'Create New Manager'}</h1>
        <p style={styles.subtitle}>
          {id ? 'Update the manager information below' : 'Fill in the details to create a new manager'}
        </p>
      </div>

      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.formGroup}>
          <label style={styles.label}>First Name *</label>
          <input
            type="text"
            style={styles.input}
            value={formData.first_name}
            onChange={(e) => setFormData({...formData, first_name: e.target.value})}
            required
            disabled={loading}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Last Name *</label>
          <input
            type="text"
            style={styles.input}
            value={formData.last_name}
            onChange={(e) => setFormData({...formData, last_name: e.target.value})}
            required
            disabled={loading}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Email *</label>
          <input
            type="email"
            style={styles.input}
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            required
            disabled={loading}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Department *</label>
          <input
            type="text"
            style={styles.input}
            value={formData.department}
            onChange={(e) => setFormData({...formData, department: e.target.value})}
            placeholder="e.g., Engineering, Product, Design, Marketing"
            required
            disabled={loading}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Status *</label>
          <select
            style={styles.input}
            value={formData.status}
            onChange={(e) => setFormData({...formData, status: e.target.value})}
            required
            disabled={loading}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <p style={{fontSize: '0.875rem', color: '#6c757d', margin: '0.5rem 0 0 0'}}>
            Only active managers will appear in survey creation dropdowns
          </p>
        </div>

        <div style={styles.buttonGroup}>
          <button 
            type="submit" 
            style={{...styles.submitBtn, ...(loading ? styles.disabledBtn : {})}}
            disabled={loading}
          >
            {loading ? 'Saving...' : (id ? 'Update Manager' : 'Create Manager')}
          </button>
          <button 
            type="button"
            style={styles.cancelBtn}
            onClick={() => navigate('/admin/managers')}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default ManagerForm;
